import type { MouseEvent, ReactNode } from 'react';
import { useMenu } from './Menu';
import { ICONS, ICON_VIEWBOX, type IconName } from './icons';
import { BrandMark, Icon } from './primitives';

export interface TeamOption {
  id: string;
  name: string;
  role: string;
}

type LinkTo = (href: string) => { href: string; onClick?: (event: MouseEvent) => void };

function RoleChip({ role }: { role: string }) {
  return <span className="bf-role">{role}</span>;
}

/** One line of the account's menu. */
type AccountEntry =
  | { kind: 'team'; team: TeamOption }
  | { kind: 'create' }
  /** `outside` opens in a tab of its own: it leaves the dashboard. */
  | { kind: 'link'; label: string; icon: IconName; href: string; onClick?: (event: MouseEvent) => void; current?: boolean; outside?: boolean }
  /** The dashboard drawn dark: a switch, which leaves the menu open so its effect is seen. */
  | { kind: 'theme' }
  | { kind: 'signout' };

/**
 * The account, and everything that is about *who* is working rather than *what on*: which team,
 * the account's settings, the guides, and the way out.
 *
 * **Who you are is two lines on the bar** — the username, and under it the team in hand. They are
 * the answer to the same question, and a creator in three teams needs the second line more than
 * the first.
 *
 * **Switching team is in here, not beside the logo.** It used to be a control of its own, the
 * widest thing on the bar, for something most accounts never do: most are in one team. Here it
 * costs the bar nothing, and the list reads as what it is — the teams *this account* is in, each
 * with the role it holds there. A custom list rather than a `<select>`: rows are real links, so a
 * team can be opened in a new tab, and it ends with a create action. "New team" is always offered
 * — hiding it once you had one made a second impossible.
 *
 * **Guides and the console are here and nowhere else on the bar.** They were on both, which was
 * one thing said twice. The console's entry is absent for an account without one, not disabled
 * (`docs/admin-plan.md` §D4).
 *
 * **The theme is here too**, beside the account's settings: it is a preference of whoever is
 * working, kept for this browser (`theme.ts`). The kit is told which it is and asked to change
 * it; it holds neither.
 */
function AccountMenu({
  username,
  email,
  teams,
  team,
  current,
  linkTo,
  guidesHref,
  adminUrl,
  theme,
  onTheme,
  onSelectTeam,
  onCreateTeam,
  onSignOut,
}: {
  username: string;
  email: string;
  teams: TeamOption[];
  team: TeamOption | undefined;
  current: boolean;
  linkTo: LinkTo;
  guidesHref: string;
  adminUrl?: string;
  theme?: 'light' | 'dark';
  onTheme?: (theme: 'light' | 'dark') => void;
  onSelectTeam?: (id: string) => void;
  onCreateTeam?: () => void;
  onSignOut: () => void;
}) {
  const entries: AccountEntry[] = [
    ...teams.map((option): AccountEntry => ({ kind: 'team', team: option })),
    ...(onCreateTeam ? ([{ kind: 'create' }] satisfies AccountEntry[]) : []),
    { kind: 'link', label: 'Account', icon: 'person', ...linkTo('/account'), current },
    ...(onTheme ? ([{ kind: 'theme' }] satisfies AccountEntry[]) : []),
    { kind: 'link', label: 'Guides', icon: 'help', href: guidesHref, outside: true },
    ...(adminUrl ? ([{ kind: 'link', label: 'Admin', icon: 'tune', href: adminUrl }] satisfies AccountEntry[]) : []),
    { kind: 'signout' },
  ];
  // Opens on the team in hand, so the arrow keys start from where you are.
  const menu = useMenu(entries.length, Math.max(0, teams.findIndex((option) => option.id === team?.id)));
  // What the account is called. The address stands in for an account whose name has not arrived.
  const name = username || email;
  const firstLink = entries.findIndex((entry) => entry.kind === 'link');

  return (
    <div className="bf-menu-anchor is-account">
      <button
        ref={menu.triggerRef}
        type="button"
        className={current || menu.open ? 'bf-account is-on' : 'bf-account'}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={team ? `Account: ${name}, in ${team.name}` : `Account: ${name}`}
        onClick={() => menu.setOpen((v) => !v)}
        onKeyDown={menu.onTriggerKeyDown}
      >
        <span className="bf-avatar">{(name || '?').slice(0, 1).toUpperCase()}</span>
        <span className="bf-account-lines">
          <span className="bf-account-name">{name}</span>
          {team ? <span className="bf-account-team">{team.name}</span> : null}
        </span>
        <Icon name={menu.open ? 'expand_less' : 'expand_more'} size={18} style={{ color: 'var(--ink-muted)' }} />
      </button>
      {menu.open ? (
        <div ref={menu.menuRef} role="menu" className="bf-menu bf-menu-account" onKeyDown={menu.onMenuKeyDown}>
          <p className="bf-menu-label">{email}</p>
          {entries.map((entry, index) => {
            const common = { role: 'menuitem', 'data-menuitem': true, tabIndex: index === menu.cursor ? 0 : -1 } as const;
            if (entry.kind === 'team') {
              const option = entry.team;
              const pick = () => {
                onSelectTeam?.(option.id);
                menu.close(true);
              };
              return (
                <div key={option.id} role="none">
                  {index === 0 ? (
                    <>
                      <div className="bf-menu-rule" />
                      <p className="bf-menu-label is-heading">Teams</p>
                    </>
                  ) : null}
                  <a
                    {...common}
                    href={`/teams/${option.id}/games`}
                    className="bf-menu-item"
                    aria-current={option.id === team?.id ? 'true' : undefined}
                    onClick={(event) => {
                      event.preventDefault();
                      pick();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        pick();
                      }
                    }}
                  >
                    <span className="bf-menu-item-label">{option.name}</span>
                    <RoleChip role={option.role} />
                    {/* The slot is kept for every row, so the roles line up whichever has the tick. */}
                    <span className="bf-menu-tick">
                      {option.id === team?.id ? <Icon name="check" size={20} style={{ color: 'var(--purple)' }} /> : null}
                    </span>
                  </a>
                </div>
              );
            }
            if (entry.kind === 'create') {
              return (
                <div key="create" role="none">
                  {teams.length === 0 ? <div className="bf-menu-rule" /> : null}
                  <button
                    type="button"
                    {...common}
                    className="bf-menu-item bf-menu-item-create"
                    onClick={() => {
                      onCreateTeam?.();
                      menu.close(true);
                    }}
                  >
                    <Icon name="add" size={20} />
                    New team
                  </button>
                </div>
              );
            }
            if (entry.kind === 'link') {
              return (
                <div key={entry.label} role="none">
                  {index === firstLink ? <div className="bf-menu-rule" /> : null}
                  <a
                    {...common}
                    href={entry.href}
                    className="bf-menu-item"
                    aria-current={entry.current ? 'page' : undefined}
                    {...(entry.outside ? { target: '_blank', rel: 'noopener' } : {})}
                    onClick={(event) => {
                      menu.setOpen(false);
                      entry.onClick?.(event);
                    }}
                  >
                    <Icon name={entry.icon} size={20} />
                    <span className="bf-menu-item-label">{entry.label}</span>
                  </a>
                </div>
              );
            }
            if (entry.kind === 'theme') {
              const dark = theme === 'dark';
              return (
                <div key="theme" role="none">
                  <button
                    type="button"
                    {...common}
                    role="menuitemcheckbox"
                    aria-checked={dark}
                    className="bf-menu-item"
                    onClick={() => onTheme?.(dark ? 'light' : 'dark')}
                  >
                    <Icon name="dark_mode" size={20} />
                    <span className="bf-menu-item-label">Dark theme</span>
                    <span className={dark ? 'bf-menu-switch is-on' : 'bf-menu-switch'} aria-hidden="true">
                      <span className="bf-menu-switch-knob" />
                    </span>
                  </button>
                </div>
              );
            }
            return (
              <div key="signout" role="none">
                <div className="bf-menu-rule" />
                <button
                  type="button"
                  {...common}
                  className="bf-menu-item"
                  onClick={() => {
                    menu.setOpen(false);
                    onSignOut();
                  }}
                >
                  <Icon name="exit_to_app" size={20} />
                  <span className="bf-menu-item-label">Sign out</span>
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export interface AppBarProps {
  teams?: TeamOption[];
  /**
   * The team in hand: the one the page is about, or — on a page that is about none, the account's —
   * the one last worked in. It is what Games and Team settings lead to, and the second line under
   * the username.
   */
  activeTeamId?: string;
  onSelectTeam?: (id: string) => void;
  onCreateTeam?: () => void;
  /** What the account is called. Empty until it is known, and the address stands in. */
  username: string;
  email: string;
  currentPath: string;
  /** The guides for creators, which the account's menu opens in a tab of its own. */
  guidesHref: string;
  /** The library — the other side of the site, one press away from every page of this one. */
  libraryHref: string;
  onSignOut: () => void;
  /**
   * Anchor props for a link the kit renders itself, so it routes client-side without the kit
   * knowing about the router. Falls back to a plain `href`, which reloads the page.
   */
  linkTo?: LinkTo;
  brandHref?: string;
  /**
   * The admin console, for the accounts that have one (`docs/admin-plan.md` §5).
   *
   * A different origin and a different Worker, so it is an ordinary external anchor rather than a
   * route — and **absent for everybody else**, not disabled. That is the one place in this file
   * that overrides the standing rule about keeping role-gated controls visible: a non-admin must
   * not be able to learn the console exists, which a greyed-out link would tell them (§D4).
   */
  adminUrl?: string;
  /** The theme the dashboard is drawn in, and the way to change it (`theme.ts`). */
  theme?: 'light' | 'dark';
  onTheme?: (theme: 'light' | 'dark') => void;
  /**
   * Opens the creators' assistant (`docs/assistant-plan.md` §10.1). Passed only for an account
   * that has it, on the creators' side, off the playground — and with no `onAsk` there is no
   * button: hidden, not disabled, as every gated control is.
   */
  onAsk?: () => void;
  /** The assistant's panel is open, so the button reads as pressed. */
  askOpen?: boolean;
}

/**
 * Ask's sparkle, filled with the same colours as the ring around the button (`.bf-ask` in
 * `kit.css`). Its own SVG because an `Icon` is one colour, `currentColor`. A fixed gradient id is
 * enough: there is one Ask in a document.
 */
function AskSparkle() {
  return (
    <svg
      className="bf-ask-sparkle"
      width={20}
      height={20}
      viewBox={ICON_VIEWBOX}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="bf-ask-ink" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="bf-ask-stop-1" />
          <stop offset="0.55" className="bf-ask-stop-2" />
          <stop offset="1" className="bf-ask-stop-3" />
        </linearGradient>
      </defs>
      <path d={ICONS.auto_awesome} fill="url(#bf-ask-ink)" />
    </svg>
  );
}

/**
 * Dashboard chrome, on one row (`docs/library-first-plan.md` §8.1): the brand and whose side of
 * the site this is, three places to go, and — pushed to the right — who is working.
 *
 * **The three places look the same, because they are the same kind of thing.** Library, Games and
 * Team settings are each a destination, an icon and a word, and the one you are on is marked. The
 * library is first: it is the way back to the other half of the site, and it sits where the eye
 * starts. That it is a different host is nothing a creator needs the bar to tell them.
 *
 * **Games and Team settings are the team's, and say so by where they lead** — to the team in
 * hand, which the control on the right names. With no team at all there is nothing for either to
 * point at, and they are not drawn.
 *
 * **Everything about who is working is one control** (`AccountMenu`): the username, the team,
 * switching teams, the account's settings, the guides, signing out.
 */
export function AppBar({
  teams = [],
  activeTeamId,
  onSelectTeam,
  onCreateTeam,
  username,
  email,
  currentPath,
  guidesHref,
  libraryHref,
  onSignOut,
  linkTo = (href) => ({ href }),
  brandHref = '/',
  adminUrl,
  theme,
  onTheme,
  onAsk,
  askOpen = false,
}: AppBarProps) {
  const team = teams.find((option) => option.id === activeTeamId);
  const games = team ? `/teams/${team.id}/games` : null;
  const settings = team ? `/teams/${team.id}` : null;
  const places: { label: string; icon: IconName; href: string; current: boolean }[] = [
    { label: 'Library', icon: 'local_library', href: libraryHref, current: false },
    // A game's own page reads as being inside Games, by either address a game has had.
    ...(games
      ? [{ label: 'Games', icon: 'sports_esports' as const, href: games, current: currentPath.startsWith(games) || currentPath.startsWith('/games/') }]
      : []),
    ...(settings ? [{ label: 'Team settings', icon: 'settings' as const, href: settings, current: currentPath === settings }] : []),
  ];

  return (
    <header className="bf-appbar">
      <div className="bf-appbar-top">
        <a className="bf-wordmark" href={brandHref}>
          <BrandMark />
          BoardFlight
          <span className="bf-wordmark-side">Creators</span>
        </a>
        <nav className="bf-appbar-nav" aria-label="Dashboard">
          {places.map((place) => (
            <a key={place.label} className="bf-navlink" aria-current={place.current ? 'page' : undefined} {...linkTo(place.href)}>
              <Icon name={place.icon} size={20} />
              <span className="bf-navlink-label">{place.label}</span>
            </a>
          ))}
        </nav>
        <span className="bf-grow" />
        {onAsk ? (
          <button type="button" className="bf-navlink bf-ask" aria-pressed={askOpen} onClick={onAsk}>
            <AskSparkle />
            <span className="bf-navlink-label">Ask</span>
          </button>
        ) : null}
        <AccountMenu
          username={username}
          email={email}
          teams={teams}
          team={team}
          current={currentPath === '/account'}
          linkTo={linkTo}
          guidesHref={guidesHref}
          adminUrl={adminUrl}
          theme={theme}
          onTheme={onTheme}
          onSelectTeam={onSelectTeam}
          onCreateTeam={onCreateTeam}
          onSignOut={onSignOut}
        />
      </div>
    </header>
  );
}

/*
 * `Breadcrumbs` was here: team / game / Channels, above a page head that a channel had taken
 * over. It went with the game screen's navigation column (`SideNav.tsx`), which says all three
 * things at once and keeps saying them while you read — a trail is what a screen needs when the
 * thing you came from is no longer on it. The one crumb that is left, the way back out to the
 * team, is a plain `.bf-back` link the page passes as `back`.
 */

export interface ShellProps {
  children: ReactNode;
  /** A back link above the title — "Acme Games" on a game detail page. */
  back?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Beside the title and subtitle, spanning both — the game's icon on a game detail page. */
  icon?: ReactNode;
  action?: ReactNode;
  /** Above the title: the page failed, but the chrome around it still works. */
  notice?: ReactNode;
  /**
   * Drop the reading measure and use the whole window.
   *
   * For a screen that is a *table* rather than prose. The 1240px cap is a measure for text and
   * forms, and it is the right default for most of the dashboard — but a games list carries a
   * package id, a platform, a set of channel chips and a date on one line, and capping it made
   * five columns share the width of a paragraph. Opt in per screen rather than raising the cap
   * for everything, because the pages that are forms still want the measure.
   */
  wide?: boolean;
  /**
   * A navigation column down the left of the content — `SideNav` on the game screen.
   *
   * Below the page head rather than beside it, because the head names the thing the whole screen
   * is about and the column names a place *within* it. It sits outside the scroll of the content
   * and stays put while a build timeline runs on for a screen and a half.
   */
  nav?: ReactNode;
}

/**
 * The content box every signed-in screen renders into.
 *
 * One layout component with screens as children, so the four dashboard pages cannot drift
 * apart on their gutters, their title treatment or where the page action sits.
 */
export function Shell({
  children,
  back,
  title,
  subtitle,
  icon,
  action,
  notice,
  wide = false,
  nav,
}: ShellProps) {
  return (
    <main
      className={
        [
          'bf-shell',
          wide ? 'is-wide' : '',
          /* The measure belongs to the *content*, so a navigation column is added outside it
             rather than taken out of it — otherwise a channel's seven tabs, which fitted across
             1240px for as long as they have existed, arrive on this screen already clipped. */
          nav ? 'has-nav' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
    >
      {notice}
      {back}
      {title ? (
        <div className="bf-page-head">
          <div className={icon ? 'bf-page-headtext has-icon' : 'bf-page-headtext'}>
            {icon ? <div className="bf-page-icon">{icon}</div> : null}
            <div className="bf-page-headlines">
              <h1 className="bf-page-title">{title}</h1>
              {subtitle ? <p className="bf-page-sub">{subtitle}</p> : null}
            </div>
          </div>
          {action}
        </div>
      ) : null}
      {/* The extra wrapper only exists when there is a column to put beside the content — every
          other screen keeps rendering its children straight into the shell's own flex column. */}
      {nav ? (
        <div className="bf-withnav">
          {nav}
          <div className="bf-withnav-main">{children}</div>
        </div>
      ) : (
        children
      )}
    </main>
  );
}
